/* ==========================================================================
   Empire Relic — theme.js
   Vanilla JS: drawers, cart, predictive search, carousels, hero slideshow.
   No external dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.routes || {};

  /* ---------- Helpers ---------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function trapFocus(container) {
    var focusable = qsa('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKeydown);
    first.focus();
    return function () { container.removeEventListener('keydown', onKeydown); };
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ---------- Cart API ---------- */
  var Cart = {
    sectionId: 'cart-drawer-items',

    refresh: function () {
      return fetch(routes.cart_url + '?sections=' + Cart.sectionId)
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          var html = sections[Cart.sectionId];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-items]');
          var target = qs('[data-cart-drawer-content]');
          if (fresh && target) { target.innerHTML = ''; target.appendChild(fresh); }
          var count = fresh ? parseInt(fresh.getAttribute('data-item-count'), 10) : null;
          if (count !== null && !isNaN(count)) Cart.updateCount(count);
        });
    },

    updateCount: function (count) {
      qsa('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.toggleAttribute('hidden', count === 0);
      });
      qsa('.yp-cart').forEach(function (link) {
        link.setAttribute('aria-label', 'Shopping bag, ' + count + (count === 1 ? ' item' : ' items'));
      });
    },

    add: function (formData) {
      return fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      });
    },

    changeLine: function (line, quantity) {
      return fetch(routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      });
    },

    updateNote: debounce(function (note) {
      fetch(routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note })
      });
    }, 400)
  };
  window.themeCart = Cart;

  /* ---------- Cart drawer ---------- */
  var cartDrawer = qs('[data-cart-drawer]');
  var releaseCartFocus = null;
  var cartTrigger = null;

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartTrigger = document.activeElement;
    cartDrawer.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    releaseCartFocus = trapFocus(qs('.cart-drawer__panel', cartDrawer));
  }

  function closeCartDrawer() {
    if (!cartDrawer || cartDrawer.hidden) return;
    cartDrawer.setAttribute('hidden', '');
    document.body.style.overflow = '';
    if (releaseCartFocus) releaseCartFocus();
    if (cartTrigger) cartTrigger.focus();
  }
  window.openCartDrawer = openCartDrawer;

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-cart-drawer-open]');
    if (openBtn && cartDrawer) {
      e.preventDefault();
      openCartDrawer();
      return;
    }
    if (e.target.closest('[data-cart-drawer-close]')) closeCartDrawer();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeCartDrawer(); closeMenuDrawer(); }
  });

  /* ---------- Cart drawer line updates (delegated, survives re-render) ---------- */
  var cartBusy = false;

  document.addEventListener('click', function (e) {
    var changeBtn = e.target.closest('[data-quantity-change]');
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (!changeBtn && !removeBtn) return;
    if (!e.target.closest('[data-cart-items]')) return;
    if (cartBusy) return;

    var line, quantity;
    if (removeBtn) {
      line = parseInt(removeBtn.getAttribute('data-line'), 10);
      quantity = 0;
    } else {
      line = parseInt(changeBtn.getAttribute('data-line'), 10);
      var input = qs('[data-quantity-input][data-line="' + line + '"]');
      var delta = parseInt(changeBtn.getAttribute('data-quantity-change'), 10);
      quantity = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
    }
    cartBusy = true;
    var onCartPage = !!e.target.closest('.cart-page');
    Cart.changeLine(line, quantity)
      .then(function () {
        if (onCartPage) { window.location.reload(); return; }
        return Cart.refresh();
      })
      .catch(function () { alert(window.cartStrings.error); })
      .finally(function () { cartBusy = false; });
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-quantity-input]');
    if (input && e.target.closest('[data-cart-items]')) {
      var line = parseInt(input.getAttribute('data-line'), 10);
      var quantity = Math.max(0, parseInt(input.value, 10) || 0);
      var onCartPage = !!e.target.closest('.cart-page');
      Cart.changeLine(line, quantity)
        .then(function () {
          if (onCartPage) { window.location.reload(); return; }
          return Cart.refresh();
        })
        .catch(function () { alert(window.cartStrings.error); });
    }
    var note = e.target.closest('[data-cart-note]');
    if (note) Cart.updateNote(note.value);
  });

  /* ---------- Product form (add to cart) — custom element ---------- */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
      onSubmit(e) {
        e.preventDefault();
        var button = this.form.querySelector('[data-add-to-cart]');
        var text = this.form.querySelector('[data-add-to-cart-text]');
        if (button && button.hasAttribute('disabled')) return;
        if (button) button.setAttribute('disabled', '');
        Cart.add(new FormData(this.form))
          .then(function () { return Cart.refresh(); })
          .then(function () {
            if (text) {
              var original = text.textContent;
              text.textContent = window.variantStrings.added;
              setTimeout(function () { text.textContent = original; }, 1500);
            }
            if (document.querySelector('[data-cart-drawer]')) openCartDrawer();
            else window.location.href = routes.cart_url;
          })
          .catch(function (err) {
            var message = (err && err.description) || window.cartStrings.error;
            alert(message);
          })
          .finally(function () { if (button) button.removeAttribute('disabled'); });
      }
    });
  }

  /* ---------- Menu drawer ---------- */
  var menuDrawer = qs('[data-menu-drawer]');
  var releaseMenuFocus = null;
  var menuTrigger = null;

  function openMenuDrawer() {
    if (!menuDrawer) return;
    menuTrigger = document.activeElement;
    menuDrawer.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    releaseMenuFocus = trapFocus(qs('.menu-drawer__panel', menuDrawer));
  }
  function closeMenuDrawer() {
    if (!menuDrawer || menuDrawer.hidden) return;
    menuDrawer.setAttribute('hidden', '');
    document.body.style.overflow = '';
    if (releaseMenuFocus) releaseMenuFocus();
    if (menuTrigger) menuTrigger.focus();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-menu-drawer-open]')) { e.preventDefault(); openMenuDrawer(); }
    if (e.target.closest('[data-menu-drawer-close]')) closeMenuDrawer();
  });

  /* ---------- Disclosure (localization selectors) ---------- */
  qsa('[data-disclosure]').forEach(function (disclosure) {
    var button = qs('.disclosure__button', disclosure);
    var list = qs('.disclosure__list', disclosure);
    if (!button || !list) return;
    button.addEventListener('click', function () {
      var expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      list.toggleAttribute('hidden', expanded);
    });
    document.addEventListener('click', function (e) {
      if (!disclosure.contains(e.target)) {
        button.setAttribute('aria-expanded', 'false');
        list.setAttribute('hidden', '');
      }
    });
    qsa('.disclosure__item', disclosure).forEach(function (item) {
      item.addEventListener('click', function () {
        var input = qs('input[data-disclosure-input]', disclosure);
        if (input) input.value = item.getAttribute('data-value');
        disclosure.closest('form').submit();
      });
    });
  });

  /* ---------- Predictive search ---------- */
  qsa('[data-predictive-search]').forEach(function (container) {
    var input = qs('input[type="search"], input[name="q"]', container);
    var results = qs('[data-predictive-search-results]', container);
    if (!input || !results) return;

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) { results.setAttribute('hidden', ''); results.innerHTML = ''; return; }
      var url = routes.predictive_search_url +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,query,page' +
        '&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { renderResults(term, data.resources.results); })
        .catch(function () { results.setAttribute('hidden', ''); });
    }, 250);

    function renderResults(term, res) {
      var html = '';
      var queries = res.queries || [];
      var products = res.products || [];
      var pages = res.pages || [];

      if (queries.length) {
        html += '<p class="predictive-search__heading">' + container.getAttribute('data-label-suggestions') + '</p>';
        queries.forEach(function (q) {
          html += '<a class="predictive-search__item" href="' + q.url + '"><span class="predictive-search__item-title">' + q.styled_text + '</span></a>';
        });
      }
      if (products.length) {
        html += '<p class="predictive-search__heading">' + container.getAttribute('data-label-products') + '</p>';
        products.forEach(function (p) {
          var img = p.featured_image && p.featured_image.url
            ? '<img src="' + p.featured_image.url + '&width=84" alt="" width="42" height="42" loading="lazy">'
            : '';
          html += '<a class="predictive-search__item" href="' + p.url + '">' + img +
            '<span class="predictive-search__item-title">' + escapeHtml(p.title) + '</span>' +
            '<span class="predictive-search__item-price">' + escapeHtml(p.price ? formatMoneyFallback(p.price) : '') + '</span></a>';
        });
      }
      if (pages.length) {
        html += '<p class="predictive-search__heading">' + container.getAttribute('data-label-pages') + '</p>';
        pages.forEach(function (pg) {
          html += '<a class="predictive-search__item" href="' + pg.url + '"><span class="predictive-search__item-title">' + escapeHtml(pg.title) + '</span></a>';
        });
      }
      if (!html) {
        results.setAttribute('hidden', '');
        results.innerHTML = '';
        return;
      }
      html += '<a class="predictive-search__all" href="' + routes.predictive_search_url.replace('/suggest', '') +
        '?q=' + encodeURIComponent(term) + '">' + container.getAttribute('data-label-all') + '</a>';
      results.innerHTML = html;
      results.removeAttribute('hidden');
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }
    function formatMoneyFallback(price) { return price; }

    input.addEventListener('input', run);
    input.addEventListener('focus', function () { if (results.innerHTML) results.removeAttribute('hidden'); });
    document.addEventListener('click', function (e) {
      if (!container.contains(e.target)) results.setAttribute('hidden', '');
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { results.setAttribute('hidden', ''); }
    });
  });

  /* ---------- Product carousels ---------- */
  qsa('[data-category-nav]').forEach(function (nav) {
    var track = qs('[data-category-nav-track]', nav);
    if (!track) return;
    var prev = qs('[data-category-nav-prev]', nav);
    var next = qs('[data-category-nav-next]', nav);
    function updateArrows() {
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    qsa('[data-category-nav-prev], [data-category-nav-next]', nav).forEach(function (button) {
      button.addEventListener('click', function () {
        var direction = button.hasAttribute('data-category-nav-next') ? 1 : -1;
        var distance = Math.max(240, Math.round(track.clientWidth * 0.7));
        track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      });
    });
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();
  });

  qsa('[data-carousel]').forEach(function (carousel) {
    var track = qs('[data-carousel-track]', carousel);
    if (!track) return;
    qsa('[data-carousel-prev], [data-carousel-next]', carousel).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = track.firstElementChild;
        var step = card ? (card.getBoundingClientRect().width + 16) * 2 : track.clientWidth * 0.8;
        var dir = btn.hasAttribute('data-carousel-next') ? 1 : -1;
        track.scrollBy({ left: step * dir, behavior: 'smooth' });
      });
    });
  });

  function initCategoryStrip(section) {
    if (section._categoryStripDestroy) return;
    var track = qs('[data-category-track]', section);
    var toggle = qs('[data-category-motion-toggle]', section);
    if (!track || !toggle || track.children.length < 2) return;

    var label = qs('[data-category-motion-label]', toggle);
    var autoplay = section.getAttribute('data-category-autoplay') !== 'false';
    var speed = Math.max(10, Math.min(50, Number(section.getAttribute('data-category-speed')) || 25));
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var controller = new AbortController();
    var signal = controller.signal;
    var visible = false;
    var hovered = false;
    var focused = false;
    var pointerActive = false;
    var userPaused = false;
    var direction = 1;
    var frame = 0;
    var lastTime = 0;
    var position = track.scrollLeft;
    var holdUntil = 0;
    var holdTimer = 0;
    var activePointerId = null;
    var drag = null;
    var suppressClickUntil = 0;
    var resizeObserver = null;

    track.classList.add('is-enhanced');

    function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function canMove() {
      return autoplay && maxScroll() > 2 && visible && !document.hidden &&
        !reducedMotion.matches && !userPaused && !hovered && !focused &&
        !pointerActive && performance.now() >= holdUntil;
    }
    function stop() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
    }
    function tick(now) {
      frame = 0;
      if (!canMove()) { stop(); return; }
      if (lastTime) {
        var end = maxScroll();
        var next = position + direction * Math.min(now - lastTime, 64) * speed / 1000;
        if (next >= end) { position = end; direction = -1; }
        else if (next <= 0) { position = 0; direction = 1; }
        else position = next;
        track.scrollLeft = position;
      }
      lastTime = now;
      frame = requestAnimationFrame(tick);
    }
    function sync() {
      var available = autoplay && maxScroll() > 2 && !reducedMotion.matches;
      toggle.hidden = !available;
      track.classList.toggle('is-animated', available);
      toggle.setAttribute('data-paused', String(userPaused));
      label.textContent = userPaused ? toggle.getAttribute('data-play-label') : toggle.getAttribute('data-pause-label');
      if (canMove()) {
        if (!frame) { lastTime = 0; position = track.scrollLeft; frame = requestAnimationFrame(tick); }
      } else stop();
    }
    function checkVisibility() {
      var bounds = section.getBoundingClientRect();
      visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
      sync();
    }
    function hold(ms) {
      holdUntil = performance.now() + ms;
      clearTimeout(holdTimer);
      holdTimer = setTimeout(sync, ms + 50);
      sync();
    }
    function endPointer(event) {
      if (event.pointerId !== activePointerId) return;
      if (drag && drag.moved) {
        suppressClickUntil = performance.now() + 400;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      }
      drag = null;
      activePointerId = null;
      pointerActive = false;
      hold(1600);
    }

    toggle.addEventListener('click', function () { userPaused = !userPaused; sync(); }, { signal: signal });
    track.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') { hovered = true; sync(); }
    }, { signal: signal });
    track.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') { hovered = false; hold(500); }
    }, { signal: signal });
    track.addEventListener('focusin', function () { focused = true; sync(); }, { signal: signal });
    track.addEventListener('focusout', function () {
      setTimeout(function () { focused = track.contains(document.activeElement); sync(); }, 0);
    }, { signal: signal });
    track.addEventListener('pointerdown', function (event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      activePointerId = event.pointerId;
      pointerActive = true;
      if (event.pointerType === 'mouse') {
        drag = { x: event.clientX, y: event.clientY, left: track.scrollLeft, moved: false };
      }
      sync();
    }, { signal: signal });
    track.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== activePointerId) return;
      var dx = event.clientX - drag.x;
      var dy = event.clientY - drag.y;
      if (!drag.moved) {
        if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
        drag.moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      track.scrollLeft = drag.left - dx;
    }, { passive: false, signal: signal });
    window.addEventListener('pointerup', endPointer, { signal: signal });
    window.addEventListener('pointercancel', endPointer, { signal: signal });
    track.addEventListener('click', function (event) {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClickUntil = 0;
    }, { capture: true, signal: signal });
    track.addEventListener('dragstart', function (event) { event.preventDefault(); }, { signal: signal });
    track.addEventListener('wheel', function () { hold(1600); }, { passive: true, signal: signal });
    document.addEventListener('visibilitychange', sync, { signal: signal });
    window.addEventListener('scroll', checkVisibility, { passive: true, signal: signal });
    window.addEventListener('resize', checkVisibility, { passive: true, signal: signal });
    reducedMotion.addEventListener('change', sync);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(sync);
      resizeObserver.observe(track);
    }
    section._categoryStripDestroy = function () {
      stop();
      clearTimeout(holdTimer);
      controller.abort();
      reducedMotion.removeEventListener('change', sync);
      if (resizeObserver) resizeObserver.disconnect();
      section._categoryStripDestroy = null;
    };
    checkVisibility();
  }
  qsa('.category-strip').forEach(initCategoryStrip);
  document.addEventListener('shopify:section:load', function (event) {
    var section = qs('.category-strip', event.target);
    if (section) initCategoryStrip(section);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var section = qs('.category-strip', event.target);
    if (section && section._categoryStripDestroy) section._categoryStripDestroy();
  });

  /* ---------- Hero slideshow ---------- */
  qsa('[data-slideshow]').forEach(function (slideshow) {
    var slides = qsa('[data-slide]', slideshow);
    if (slides.length < 2) return;
    var index = 0;
    var autoplay = slideshow.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(slideshow.getAttribute('data-autoplay-speed'), 10) || 6000;
    var timer = null;
    var dots = qsa('[data-slide-dot]', slideshow);

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) { s.toggleAttribute('hidden', n !== index); });
      dots.forEach(function (d, n) { d.setAttribute('aria-current', String(n === index)); });
    }
    function start() {
      if (!autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      timer = setInterval(function () { show(index + 1); }, interval);
    }
    function stop() { if (timer) clearInterval(timer); }

    qsa('[data-slide-prev]', slideshow).forEach(function (b) {
      b.addEventListener('click', function () { stop(); show(index - 1); start(); });
    });
    qsa('[data-slide-next]', slideshow).forEach(function (b) {
      b.addEventListener('click', function () { stop(); show(index + 1); start(); });
    });
    dots.forEach(function (d, n) {
      d.addEventListener('click', function () { stop(); show(n); start(); });
    });
    slideshow.addEventListener('mouseenter', stop);
    slideshow.addEventListener('mouseleave', start);
    show(0);
    start();
  });

  /* ---------- Back to top ---------- */
  var backToTop = qs('[data-back-to-top]');
  if (backToTop) {
    var toggleBtt = function () {
      backToTop.toggleAttribute('hidden', window.scrollY < 600);
    };
    window.addEventListener('scroll', toggleBtt, { passive: true });
    toggleBtt();
    backToTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ---------- Newsletter: preserve scroll anchor after form redirect ---------- */
  var welcomeOfferPending = false;
  try { welcomeOfferPending = sessionStorage.getItem('yorkshire-welcome-offer-pending') === '1'; } catch (error) {}
  if (window.location.hash === '#newsletter-form' || (window.location.search.indexOf('customer_posted=true') > -1 && !welcomeOfferPending)) {
    var nl = qs('.newsletter');
    if (nl) {
      window.setTimeout(function () {
        var y = nl.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo(0, y);
      }, 0);
    }
  }

  /* ---------- Facets: auto-submit sort ---------- */
  qsa('[data-sort-by]').forEach(function (select) {
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });

  /* ---------- Recently viewed: record product handles ---------- */
  var rvMeta = qs('[data-product-handle]');
  if (rvMeta) {
    try {
      var handle = rvMeta.getAttribute('data-product-handle');
      var key = 'er:recently-viewed';
      var list = JSON.parse(localStorage.getItem(key) || '[]');
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (err) { /* storage unavailable */ }
  }

  /* ---------- Recently viewed: render ---------- */
  qsa('[data-recently-viewed]').forEach(function (container) {
    var track = qs('[data-recently-viewed-track]', container);
    if (!track) return;
    var currentHandle = container.getAttribute('data-exclude-handle');
    var limit = parseInt(container.getAttribute('data-limit'), 10) || 8;
    var list = [];
    try { list = JSON.parse(localStorage.getItem('er:recently-viewed') || '[]'); } catch (err) { /* noop */ }
    list = list.filter(function (h) { return h !== currentHandle; }).slice(0, limit);
    if (!list.length) { container.closest('.shopify-section').style.display = 'none'; return; }

    Promise.all(list.map(function (h) {
      return fetch('/products/' + h + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      products = products.filter(Boolean);
      if (!products.length) { container.closest('.shopify-section').style.display = 'none'; return; }
      track.innerHTML = products.map(function (p) {
        var img = p.featured_image
          ? '<img src="' + p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=480" alt="" loading="lazy" class="product-card__image product-card__image--primary" width="480" height="480">'
          : '<div class="media-placeholder media-placeholder--square"></div>';
        return '<article class="product-card">' +
          '<div class="product-card__media"><a class="product-card__media-link" href="' + p.url + '" tabindex="-1" aria-hidden="true">' + img + '</a></div>' +
          '<div class="product-card__info">' +
          '<h3 class="product-card__title"><a class="product-card__link" href="' + p.url + '">' + escapeText(p.title) + '</a></h3>' +
          '<div class="price"><span class="price__current">' + formatMoney(p.price) + '</span></div>' +
          '</div></article>';
      }).join('');
    });

    function escapeText(str) {
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
    function formatMoney(cents) {
      var format = container.getAttribute('data-money-format') || '£{{amount}}';
      var amount = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
  });

  /* ---------- Product recommendations (lazy-loaded via Section Rendering API) ---------- */
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        var el = this;
        if (!('IntersectionObserver' in window)) return;
        var observer = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(el.dataset.url)
            .then(function (r) { return r.text(); })
            .then(function (text) {
              var doc = new DOMParser().parseFromString(text, 'text/html');
              var fresh = doc.querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim().length) el.innerHTML = fresh.innerHTML;
            })
            .catch(function () { /* leave empty */ });
        }, { rootMargin: '0px 0px 400px 0px' });
        observer.observe(this);
      }
    });
  }

  /* ---------- Graphic lightbox (click to enlarge) ---------- */
  function openLightbox(src, alt) {
    var overlay = document.createElement('div');
    overlay.className = 'graphic-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'graphic-lightbox__close';
    closeBtn.setAttribute('aria-label', (window.accessibilityStrings && window.accessibilityStrings.close) || 'Close');
    closeBtn.innerHTML = '&times;';
    var img = document.createElement('img');
    img.src = src;
    img.alt = alt || '';
    overlay.appendChild(closeBtn);
    overlay.appendChild(img);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    var restoreFocus = document.activeElement;
    function close() {
      overlay.remove();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (restoreFocus) restoreFocus.focus();
    }
    function onKey(ev) { if (ev.key === 'Escape') close(); }
    overlay.addEventListener('click', function (ev) {
      if (ev.target === overlay || ev.target === closeBtn) close();
    });
    document.addEventListener('keydown', onKey);
    closeBtn.focus();
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-lightbox]');
    if (!trigger) return;
    e.preventDefault();
    var img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
    if (!img) return;
    openLightbox(trigger.getAttribute('data-full') || img.currentSrc || img.src, img.alt);
  });

  /* ---------- Sync cart count on load (bfcache safety) ---------- */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) Cart.refresh();
  });
})();

